import CustomInput from 'components/common/CustomInput'
import CustomSelect from 'components/common/CustomSelect'
import { useEffect, useState } from 'react'
import { IContainerList, IPackingList, IQuoteThird } from 'types/quote'
import { booleanSelect, containerSelect, packingUnit, shipmentModeMenuSimple, ventSelect } from 'data/quote'
import { ReactComponent as Info } from 'assets/common/info.svg'
import { ReactComponent as AddW } from 'assets/quote/addW.svg'
import { calcWeight } from 'components/function'
import HsCode from 'components/common/HsCode'
import AreaTitle from 'components/quote/AreaTitle'
import CreateArea from 'components/quote/CreateArea'
import { toast } from 'react-toastify'
import { ReactComponent as FileList } from 'assets/common/fileList.svg'
import { ReactComponent as Bin } from 'assets/quote/bin.svg'
import { ReactComponent as Frozen } from 'assets/quote/frozen.svg'
import { ReactComponent as Danger } from 'assets/quote/danger.svg'
import DragFileMulti from 'components/common/DragFileMulti'
import { ReactComponent as DelIcon } from 'assets/common/fileDel.svg'
import { useAtom } from 'jotai'
import { containerFileAtom, quoteFileAtom } from 'atom/quote'

const Third = ({
    quoteInfo,
    setQuoteInfo,
    setPopWeightOpen,
    setDangerFileArr,
    setPopDangerListOpen,
    containerInfo,
    setContainerInfo,
    packingInfo,
    setPackingInfo
}: IQuoteThird) => {
    const [containerFile, setContainerFile] = useAtom(containerFileAtom);
    const [fileArr, setFileArr] = useAtom(quoteFileAtom)
    const [cbm, setCbm] = useState<number>(0);
    const [totalWeight, setTotalWeight] = useState<number>(0);
    const [totalCW, setTotalCW] = useState<number>(0);

    const tHeader = [
        { label: "No.", flex: 0.5, type: '' },
        { label: "CNTR", flex: 1, type: '' },
        { label: "G.Weight", flex: 1, type: '' },
        { label: "수량", flex: 1, type: '' },
        { label: "FLEXI BAG", flex: 1, type: 'FCL' },
        { label: "냉장/냉동", flex: 1, type: 'FCL' },
        { label: "위험물", flex: 1, type: 'FCL' },
        { label: "상태", flex: 0.5, type: '' },
    ];

    const tHeader2 = [
        { label: "No.", flex: 0.5 },
        { label: "Packing Unit", flex: 1.5 },
        { label: "수량", flex: 1 },
        { label: "Dimension(cm)", flex: 1.5 },
        { label: quoteInfo?.step1?.transportType === 'AIR' ? "C.Weight" : "Weight", flex: 1 },
        { label: "다단 적재", flex: 1 },
        { label: "상태", flex: 0.5 },
    ];

    useEffect(() => {
        const packingList = quoteInfo?.step2?.packingList ?? [];
        let cbmSum = 0, weightSum = 0, cwSum = 0;

        packingList.map(arr => {
            if (arr.width && arr.height && arr.length) {
                cbmSum += (arr.width * arr.height * arr.length) / (100 ** 3)
            }
            if (arr.weight && arr.quantity) {
                weightSum += (arr.weight * Number(arr.quantity))
            }
            if (arr.chargeableWeight) {
                cwSum += arr.chargeableWeight
            }
        })
        setQuoteInfo({
            ...quoteInfo,
            cbm: cbmSum,
            totalWeight: weightSum,
            totalCW: cwSum,
        })
        setCbm(cbmSum)
        setTotalWeight(weightSum)
        setTotalCW(cwSum)
    }, [quoteInfo?.step2?.packingList])

    useEffect(() => {
        const isValid =
            typeof packingInfo.width === 'number' && packingInfo.width > 0 &&
            typeof packingInfo.length === 'number' && packingInfo.length > 0 &&
            typeof packingInfo.height === 'number' && packingInfo.height > 0 &&
            typeof packingInfo.weight === 'number' && packingInfo.weight > 0 &&
            typeof packingInfo.quantity === 'number' && packingInfo.quantity > 0;

        if (!isValid) return;

        const { volumeWeight, actualWeight, chargeableWeight } = calcWeight({
            width: packingInfo.width,
            length: packingInfo.length,
            height: packingInfo.height,
            weight: packingInfo.weight,
            quantity: packingInfo.quantity,
        });

        if (
            packingInfo.volumeWeight !== volumeWeight ||
            packingInfo.actualWeight !== actualWeight ||
            packingInfo.chargeableWeight !== chargeableWeight
        ) {
            setPackingInfo(prev => ({
                ...prev,
                volumeWeight,
                actualWeight,
                chargeableWeight,
            }));
        }
    }, [packingInfo]);


    const addPackingItem = () => {
        if (!validatePackingItem(packingInfo)) {
            toast.error('포장 정보 필수값을 입력해주세요.')
            return
        }
        setQuoteInfo(p => ({
            ...p,
            step2: {
                ...p.step2!,
                packingList: [
                    ...(p.step2?.packingList ?? []),
                    packingInfo,
                ],
            },
        }));
        setPackingInfo({} as IPackingList)
    };


    const validatePackingItem = (item?: IPackingList, isGage: boolean = false) =>
        !!item &&
        typeof item.length === 'number' &&
        typeof item.width === 'number' &&
        typeof item.height === 'number' &&
        typeof item.weight === 'number' &&
        !!item.packageType &&
        typeof item.quantity === 'number' &&
        typeof item.isStackable === 'boolean' &&
        (isGage ? !!item.gage : true);

    const convertContainer = () => {
        let filtered = containerSelect;

        const mode = quoteInfo.step2?.shipmentMode;

        if (mode?.includes('FCL')) {
            filtered = filtered.filter((item) => item.type === 'FCL');
        }

        if (mode?.includes('OT')) {
            filtered = filtered.filter((item) => item.type === 'OT');
        }

        return filtered;
    };

    const addContainerItem = () => {
        if (!containerInfo.containerType) return toast.error('컨테이너 타입을 정해주세요.')
        if (!containerInfo.containerWeight) return toast.error('컨테이너 중량을 넣어주세요.')
        if (!containerInfo.containerQuantity) return toast.error('컨테이너 수량을 넣어주세요.')

        if (containerInfo.require === 'flexi') {
            if (typeof containerInfo.flexiBag !== 'boolean') return toast.error('FLEXI BAG을 선택주세요.')
        }

        if (containerInfo.require === 'frozen') {
            if (!containerInfo.tempMin) return toast.error('최소 온도를 넣어주세요.')
            if (!containerInfo.tempMax) return toast.error('최대 온도를 넣어주세요.')
            // if (typeof containerInfo.isVent !== 'boolean') return toast.error('VENT여부를 선택해주세요.')
        }

        if (containerInfo.isDangerous) {
            // 위험물 파일 체크해야함
            if (!containerInfo.dangerFile?.length) return toast.error('msds파일을 업로드 해주세요.')
        }

        const { dangerFile, ...rest } = containerInfo
        setContainerFile(prev => [...prev, dangerFile ?? []]);
        setQuoteInfo(p => {
            return {
                ...p,
                step2: {
                    ...p.step2!,
                    containerList: [
                        ...(p.step2?.containerList ?? []),
                        rest,
                    ],
                },
            }
        });
        setContainerInfo({} as IContainerList)
    };
    return (
        <AreaTitle title='화물정보 입력' step='02' zIndex='z-30'>
            <div className='flex flex-col gap-6 px-10 tablet:px-4 py-6'>
                {/* 종류 */}
                {
                    quoteInfo?.step1?.transportType?.includes('SEA') &&
                    <CreateArea title='종류' isRequired>
                        <div className='flex items-center border-1 rounded w-[724px] tablet:w-full'>
                            {
                                shipmentModeMenuSimple.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex-1 py-3 rounded text-center border-1 transition-all cursor-pointer
                                    ${quoteInfo.step2?.shipmentMode === item.value ? 'text-primary border-primary' : 'text-[#ECEEF2] border-transparent'}`}
                                        onClick={() => {
                                            setQuoteInfo(p => ({
                                                ...p,
                                                step2: {
                                                    ...p.step2,
                                                    shipmentMode: item.value,
                                                    containerList: [],
                                                    packingList: []
                                                }
                                            }))
                                            setContainerInfo({} as IContainerList)
                                            setPackingInfo({} as IPackingList)
                                            setFileArr([])
                                            setContainerFile([])
                                        }}
                                    >
                                        {item.label}
                                    </div>
                                ))
                            }
                        </div>
                    </CreateArea>
                }

                <CreateArea title='필수화물정보' isRequired isStart>
                    <div className='flex flex-col flex-[1] gap-6 p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg tablet:w-full max-w-[724px]'>
                        <div className='flex flex-col gap-3'>
                            <p className='text-lg'>HS CODE <span className='text-red'>*</span></p>
                            <div className='flex gap-6'>
                                <HsCode
                                    hsCode={quoteInfo?.step2?.hsCode}
                                    onChange={(value) => {
                                        setQuoteInfo(p => ({
                                            ...p,
                                            step2: {
                                                ...p.step2,
                                                hsCode: value
                                            }
                                        }))
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </CreateArea>

                {
                    quoteInfo.step1?.transportType === 'SEA' &&
                    (quoteInfo.step2?.shipmentMode?.includes('FCL') || quoteInfo.step2?.shipmentMode?.includes('OT')) &&
                    <CreateArea title='컨테이너정보' isRequired isStart>
                        <div className='p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg w-full tablet:w-full max-w-[724px]'>
                            <div className='flex flex-col gap-3 mb-6'>
                                <div className='tablet:hidden flex gap-6'>
                                    <p className='w-[170px] text-lg shrink-0'>CNTR Information <span className='text-red'>*</span></p>
                                    <p className='text-lg'>G.Weight  <span className='text-primary'>(kg)</span> <span className='text-red'>*</span></p>
                                </div>

                                <div className='flex tablet:flex-col gap-6'>
                                    <div className='flex-[1] max-w-[170px] tablet:max-w-[100%] shrink-0'>
                                        <CustomSelect
                                            height={40}
                                            fontSize={14}
                                            option={convertContainer()}
                                            value={
                                                containerInfo?.containerType !== undefined
                                                    ? convertContainer().find(arr => arr.value === containerInfo?.containerType)
                                                    : null
                                            }
                                            placeholder={'타입선택'}
                                            onChange={(_, allData) => {
                                                setContainerInfo({
                                                    ...{} as IContainerList,
                                                    containerType: allData.value,
                                                    require: allData.require,
                                                    isFrozen: allData.require === 'frozen',
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className='flex flex-[2] gap-3'>
                                        <div className='relative flex-[2]'>
                                            <CustomInput
                                                type="number" height={10} fontSize={'sm'} placeholder="컨테이너 개별 중량"
                                                value={containerInfo?.containerWeight ?? ''}
                                                max={28000}
                                                onChange={(e) => {
                                                    if (Number(e.target.value) > 28000) {
                                                        return toast.error('컨테이너 중량은 28TON을 초과할 수 없습니다.')
                                                    }
                                                    if (Number(e.target.value) > 0) {
                                                        setContainerInfo(p => ({
                                                            ...p,
                                                            containerWeight: e.target.value,
                                                        }));
                                                    }
                                                }}
                                            />
                                            <div className='top-0 right-4 absolute flex justify-center items-center h-full text-primary pointer-events-none'>Kg</div>
                                        </div>
                                        <div className='flex-[2]'>
                                            <CustomInput
                                                type="number" height={10} fontSize={'sm'} placeholder="수량입력"
                                                value={String(containerInfo?.containerQuantity) ?? ''}
                                                onChange={(e) => {
                                                    if (Number(e.target.value) > 0) {
                                                        setContainerInfo(p => ({
                                                            ...p,
                                                            containerQuantity: Number(e.target.value),
                                                        }));
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {
                                    quoteInfo.step2?.shipmentMode?.includes('FCL') &&
                                    <>
                                        {
                                            quoteInfo?.step1?.transportType === 'SEA' &&
                                            containerInfo?.require === 'flexi' &&
                                            <div className='flex flex-col gap-3'>
                                                <p className='text-lg'>FLEXI BAG <span className='text-red'>*</span></p>
                                                <div className='flex gap-6'>
                                                    <div className='flex-[1] max-w-[170px] tablet:max-w-[100%] shrink-0'>
                                                        <CustomSelect
                                                            height={40}
                                                            fontSize={14}
                                                            option={booleanSelect}
                                                            placeholder={'사용여부'}
                                                            value={
                                                                containerInfo?.flexiBag !== undefined
                                                                    ? booleanSelect.find(arr => arr.value === (containerInfo?.flexiBag ? 'Y' : 'N'))
                                                                    : null
                                                            }
                                                            onChange={(value) => {
                                                                setContainerInfo(p => ({
                                                                    ...p,
                                                                    flexiBag: value === 'Y'
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='tablet:hidden flex-[2]'></div>
                                                </div>
                                            </div>
                                        }

                                        {
                                            containerInfo?.require === 'frozen' &&
                                            <div className='flex flex-col gap-3'>
                                                <div className='flex items-center gap-2'>
                                                    <Frozen fill='#196BFF' />
                                                    <p>냉장/냉동 <span className='text-red'>*</span></p>
                                                </div>
                                                <div className='flex tablet:flex-col gap-6'>
                                                    <div className='flex flex-[1] justify-between items-center gap-2'>
                                                        <div className='relative flex-[1] w-[125px] tablet:max-w-[100%]'>
                                                            <CustomInput
                                                                type="text"
                                                                height={10}
                                                                fontSize={'sm'}
                                                                placeholder="최소온도"
                                                                value={(containerInfo?.tempMin ?? '')}
                                                                onChange={(e) => {
                                                                    const input = e.target.value;

                                                                    // 허용할 입력: 빈 값, '-', 숫자, '-숫자'
                                                                    if (/^-?\d*$/.test(input)) {
                                                                        setContainerInfo(p => ({
                                                                            ...p,
                                                                            tempMin: input === '' || input === '-' ? input : input,
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                            <div className='top-0 right-4 absolute flex justify-center items-center h-full text-primary pointer-events-none'>°C</div>
                                                        </div>
                                                        <div>~</div>
                                                        <div className='relative flex-[1] w-[125px]'>
                                                            <CustomInput
                                                                type="text"
                                                                height={10}
                                                                fontSize={'sm'}
                                                                placeholder="최대온도"
                                                                value={(containerInfo?.tempMax ?? '').toString()}
                                                                onChange={(e) => {
                                                                    const input = e.target.value;

                                                                    // 허용할 입력: 빈 값, '-', 숫자, '-숫자'
                                                                    if (/^-?\d*$/.test(input)) {
                                                                        setContainerInfo(p => ({
                                                                            ...p,
                                                                            tempMax: input === '' || input === '-' ? input : input,
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                            <div className='top-0 right-4 absolute flex justify-center items-center h-full text-primary pointer-events-none'>°C</div>
                                                        </div>
                                                    </div>
                                                    <div className='flex-[1] max-w-[170px]'>
                                                        <CustomSelect
                                                            height={40}
                                                            fontSize={14}
                                                            option={ventSelect}
                                                            placeholder={'VENT'}
                                                            value={
                                                                containerInfo?.isVent !== undefined
                                                                    ? ventSelect.find(arr => arr.value === (containerInfo.isVent ? 'Y' : 'N'))
                                                                    : null
                                                            }
                                                            onChange={(value) => {
                                                                setContainerInfo(p => ({
                                                                    ...p,
                                                                    isVent: value === 'Y' ? true : false
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                        {
                                            containerInfo?.containerType &&
                                            <div className='flex flex-col gap-3'>
                                                <div className='flex items-center gap-2'>
                                                    <Danger fill='#FF0000' />
                                                    <p>위험물</p>
                                                </div>
                                                <div className='flex tablet:flex-col gap-6'>
                                                    <div className='flex-[1] max-w-[170px] tablet:max-w-[100%] shrink-0'>
                                                        <CustomSelect
                                                            height={40}
                                                            fontSize={14}
                                                            option={booleanSelect}
                                                            placeholder={'사용여부'}
                                                            value={
                                                                containerInfo?.isDangerous !== undefined
                                                                    ? booleanSelect.find(arr => arr.value === (containerInfo?.isDangerous ? 'Y' : 'N'))
                                                                    : null
                                                            }
                                                            onChange={(value) => {
                                                                if (value === 'Y') {
                                                                    const containerList = quoteInfo.step2?.containerList;

                                                                    const shouldOpenPop =
                                                                        !Array.isArray(containerList) ||
                                                                        containerList.length === 0 ||
                                                                        containerList.some(item => typeof item.isDangerous !== 'boolean');

                                                                    if (value === 'Y' && shouldOpenPop) {
                                                                        // setPopDangerOpen?.(true);
                                                                    }

                                                                }
                                                                setContainerInfo(p => ({
                                                                    ...p,
                                                                    isDangerous: value === 'Y',
                                                                    dangerFile: []
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='flex-[2]'>
                                                        {
                                                            containerInfo?.isDangerous &&
                                                            <div className='mb-3'>
                                                                <DragFileMulti
                                                                    description='선택 또는 드래그하여<br />msds파일을 등록해 주세요'
                                                                    onChangeFileMulti={(file) => {
                                                                        if (!file) return
                                                                        console.log('file >>', file)

                                                                        setContainerInfo((p) => {
                                                                            const newArr = file.filter(
                                                                                (f) =>
                                                                                    !p.dangerFile?.some(
                                                                                        (exist) => exist.name === f.name && exist.size === f.size
                                                                                    )
                                                                            );

                                                                            return {
                                                                                ...p,
                                                                                dangerFile: [...(p.dangerFile ?? []), ...newArr],
                                                                            };
                                                                        });
                                                                    }}
                                                                />
                                                                <p className='text-[#A8A9BF] text-xs'>*파일은 PDF형식의 15MB로 제한됩니다.</p>
                                                            </div>
                                                        }
                                                        {
                                                            containerInfo?.dangerFile?.map((item: File, idx: number) => (
                                                                <div
                                                                    className='relative flex items-center gap-3 mb-3 px-6 border border-primary rounded h-10 text-primary text-sm'
                                                                    key={idx}>
                                                                    <FileList />
                                                                    {item.name}

                                                                    <DelIcon onClick={(e) => {
                                                                        e.preventDefault()
                                                                        setContainerInfo((prev) => ({
                                                                            ...prev,
                                                                            dangerFile: (prev.dangerFile ?? []).filter((_, i) => i !== idx),
                                                                        }));
                                                                    }} className="right-4 absolute cursor-pointer" />
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </>
                                }
                            </div>
                            <div className='flex justify-center items-center'>
                                <div
                                    className='flex justify-center items-center gap-2 bg-primary px-3 py-2 rounded w-full h-10 font-normal text-white text-sm text-center cursor-pointer'
                                    onClick={addContainerItem}
                                >
                                    <AddW />
                                    <p>화물 목록에 화물 정보 추가하기</p>
                                </div>
                            </div>
                        </div>
                    </CreateArea>
                }

                {
                    quoteInfo?.step2?.containerList &&
                    quoteInfo?.step2?.containerList.length > 0 &&
                    <CreateArea title='컨테이너목록' isStart>
                        <div className='flex flex-col gap-2 p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded w-full max-w-[724px] text-sm'>
                            <div className='flex bg-[#EEF4FF]'>
                                {
                                    tHeader
                                        .filter(item => quoteInfo?.step2?.shipmentMode !== 'OT' || item.type !== 'FCL')
                                        .map((item, idx) => (
                                            <div
                                                key={idx}
                                                className={`flex-[${item.flex}] flex items-center justify-center h-10`}
                                            >
                                                {item.label}
                                            </div>
                                        ))
                                }
                            </div>
                            {
                                quoteInfo?.step2?.containerList.map((item, idx) => (
                                    <div
                                        className={`flex items-center h-15 border border-[#E1E1E8] rounded`}
                                        key={idx}>
                                        <div className='flex flex-[0.5] justify-center items-center'>{idx + 1}</div>
                                        <div className='flex flex-[1] justify-center items-center'>
                                            {item.containerType}
                                        </div>
                                        <div className='flex flex-[1] justify-center items-center'>
                                            {Number(item.containerWeight).toLocaleString()} Kg
                                        </div>
                                        <div className='flex flex-[1] justify-center items-center'>
                                            {item.containerQuantity}
                                        </div>
                                        {
                                            quoteInfo?.step2?.shipmentMode !== 'OT' &&
                                            <>
                                                <div className='flex flex-[1] justify-center items-center'>
                                                    {item.flexiBag !== undefined && (item.flexiBag ? '예' : '아니요')}
                                                </div>
                                                <div className='flex flex-col flex-[1] justify-center items-center'>
                                                    <Frozen fill={item.isFrozen ? '#196BFF' : '#9D9EB7'} />
                                                    {
                                                        item.isFrozen &&
                                                        <p className='text-center leading-none'>
                                                            {item.tempMin}°C ~ {item.tempMax}°C
                                                        </p>
                                                    }
                                                    {
                                                        item.isVent !== undefined &&
                                                        <p className='text-center leading-none'>
                                                            Vent: {item.isVent ? "Open" : "Close"}
                                                        </p>
                                                    }
                                                </div>
                                                <div className={`flex-[1] gap-1 flex items-center justify-center underline text-primary ${item.isDangerous && 'cursor-pointer'}`}
                                                    onClick={() => {
                                                        if (item.isDangerous) {
                                                            setPopDangerListOpen?.(true)
                                                            setDangerFileArr?.(containerFile[idx])
                                                        }
                                                    }}
                                                >
                                                    <Danger fill={item.isDangerous ? '#FF0000' : '#9D9EB7'} />
                                                    {
                                                        item.isDangerous &&
                                                        <>
                                                            {containerFile[idx].length || 0}건
                                                        </>
                                                    }
                                                </div>
                                            </>
                                        }

                                        <div className='flex flex-[0.5] justify-center items-center cursor-pointer'
                                            onClick={() => {
                                                setQuoteInfo(prev => ({
                                                    ...prev,
                                                    step2: {
                                                        ...prev.step2,
                                                        containerList: prev.step2.containerList.filter((_, i) => i !== idx)
                                                    }
                                                }));
                                                setContainerFile(prev => prev.filter((_, i) => i !== idx));
                                            }}
                                        >
                                            <Bin />
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </CreateArea>
                }
                {
                    (quoteInfo.step1?.transportType === 'AIR' || !(quoteInfo.step2?.shipmentMode?.includes('FCL'))) &&
                    <CreateArea title='포정정보' isRequired isStart>
                        <div className='flex flex-col flex-[1] gap-6 tablet:w-full max-w-[724px]'>
                            <div className={`p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg w-full ${quoteInfo.step1?.transportType === 'AIR' ? 'order-2' : 'order-1'}`}>
                                <div className='flex flex-col gap-3'>
                                    <div className='flex gap-3'>
                                        <div className='flex tablet:flex-col flex-[2] items-center tablet:items-start gap-2'>
                                            <p>적재물품 리스트 PL</p>
                                            <div>
                                                <p className='text-red text-xs'>* PL 파일 또는 {quoteInfo?.step1?.transportType === 'AIR' ? '위' : '아래'} 정보를 적어주세요.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className='flex-1 mb-3'>
                                            <DragFileMulti
                                                description='선택 또는 드래그하여<br />PL 파일을 등록해 주세요'
                                                onChangeFileMulti={(file) => {
                                                    if (!file) return
                                                    console.log('file >>', file)
                                                    setFileArr((p) => {
                                                        const newArr = file.filter(
                                                            (file) =>
                                                                !p.some((exist) =>
                                                                    exist.name === file.name && exist.size === file.size
                                                                )
                                                        );

                                                        return [...p, ...newArr];
                                                    });
                                                }}
                                            />
                                            <p className='text-[#A8A9BF] text-xs'>*파일은 PDF형식의 15MB로 제한됩니다.</p>
                                        </div>

                                        {
                                            fileArr.map((item: File, idx: number) => (
                                                <div
                                                    className='relative flex items-center gap-3 mb-3 px-6 border border-primary rounded h-10 text-primary text-sm'
                                                    key={idx}>
                                                    <FileList />
                                                    {item.name}

                                                    <DelIcon onClick={(e) => {
                                                        e.preventDefault()
                                                        setFileArr((p) => p.filter((_, i) => i !== idx));
                                                    }} className="right-4 absolute cursor-pointer" />
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            {
                                (quoteInfo.step1?.transportType === 'AIR' || !(quoteInfo.step2?.shipmentMode?.includes('FCL'))) &&
                                <div className={`flex items-start ${quoteInfo.step1?.transportType === 'AIR' ? 'order-1' : 'order-2'}`}>
                                    <div className='relative flex-[1] p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg w-full]'>
                                        <div className='flex flex-col gap-3 mb-6'>
                                            <div className='flex flex-col gap-3'>
                                                <p>Dimension <span className='text-primary'>(cm)</span><span className='text-red'>*</span></p>
                                                <div className='flex tablet:flex-col gap-3'>
                                                    <div className='relative flex-[1]'>
                                                        <CustomInput
                                                            type="number" height={10} fontSize={'sm'} placeholder="가로"
                                                            value={(packingInfo.width ?? '')}
                                                            onChange={(e) => {
                                                                if (Number(e.target.value) > 0) {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        width: Number(e.target.value)
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                            <span className='text-primary'>Cm</span>
                                                        </div>
                                                    </div>
                                                    <div className='relative flex-[1]'>
                                                        <CustomInput
                                                            type="number" height={10} fontSize={'sm'} placeholder="세로"
                                                            value={(packingInfo?.length ?? '')}
                                                            onChange={(e) => {
                                                                if (Number(e.target.value) > 0) {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        length: Number(e.target.value)
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                            <span className='text-primary'>Cm</span>
                                                        </div>
                                                    </div>
                                                    <div className='relative flex-[1]'>
                                                        <CustomInput
                                                            type="number" height={10} fontSize={'sm'} placeholder="높이"
                                                            value={(packingInfo.height ?? '')}
                                                            onChange={(e) => {
                                                                if (Number(e.target.value) > 0) {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        height: Number(e.target.value)
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                            <span className='text-primary'>Cm</span>
                                                        </div>
                                                    </div>
                                                    <div className='relative flex-[1]'>
                                                        <CustomInput
                                                            type="number" height={10} fontSize={'sm'} placeholder="중량"
                                                            value={(packingInfo.weight ?? '')}
                                                            onChange={(e) => {
                                                                if (Number(e.target.value) > 0) {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        weight: Number(e.target.value)
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                            <span className='text-primary'>Kg</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='flex flex-col gap-3'>
                                                <div className='tablet:hidden flex gap-6'>
                                                    <p className={`flex-[${quoteInfo.step2?.shipmentMode?.includes('OT') ? '2' : '1'}]`}>
                                                        Packing Unit <span className='text-red'>*</span>
                                                    </p>
                                                    <p className='flex-1'>다단 적재 여부 <span className='text-red'>*</span></p>
                                                    {
                                                        quoteInfo.step2?.shipmentMode?.includes('OT') &&
                                                        <p className='flex-1'>IN/OUT 게이지 <span className='text-red'>*</span></p>
                                                    }
                                                </div>
                                                <div className='flex tablet:flex-col gap-3'>
                                                    <div className='flex-[1]'>
                                                        <p className={`hidden flex-[${quoteInfo.step2?.shipmentMode?.includes('OT') ? '2' : '1'}] tablet:block`}>
                                                            Packing Unit <span className='text-red'>*</span>
                                                        </p>
                                                        {
                                                            <CustomSelect
                                                                height={40}
                                                                fontSize={14}
                                                                value={
                                                                    packingInfo.packageType !== undefined ?
                                                                        packingUnit.find(arr => arr.value === packingInfo.packageType) :
                                                                        null
                                                                }
                                                                option={packingUnit}
                                                                placeholder={'포장유형'}
                                                                onChange={(value) => {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        packageType: value
                                                                    })
                                                                }}
                                                            />
                                                        }
                                                    </div>
                                                    <div className='flex-[1]'>
                                                        <CustomInput
                                                            type="number" height={10} fontSize={'sm'} placeholder="수량"
                                                            value={(packingInfo.quantity ?? '')}
                                                            onChange={(e) => {
                                                                if (Number(e.target.value) > 0) {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        quantity: Number(e.target.value)
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='flex-[1]'>
                                                        <p className='hidden tablet:block flex-1'>다단 적재 여부 <span className='text-red'>*</span></p>
                                                        {
                                                            <CustomSelect
                                                                height={40}
                                                                fontSize={14}
                                                                value={
                                                                    packingInfo.isStackable !== undefined
                                                                        ? booleanSelect.find(arr => arr.value === (packingInfo.isStackable ? 'Y' : 'N'))
                                                                        : null
                                                                }
                                                                option={booleanSelect}
                                                                placeholder={'적재여부'}
                                                                onChange={(value) => {
                                                                    setPackingInfo({
                                                                        ...packingInfo,
                                                                        isStackable: value === 'Y'
                                                                    })
                                                                }}
                                                            />
                                                        }
                                                    </div>
                                                    <div className='flex-[1]'>

                                                    </div>
                                                </div>
                                                {
                                                    quoteInfo?.step1?.transportType === 'AIR' &&
                                                    <div className='flex flex-col gap-3'>
                                                        <div className='flex gap-6'>
                                                            <p className='flex-1'>Volume Weight</p>
                                                            <p className='flex-1'>Actual Weight</p>
                                                            <div className='flex flex-1 items-center gap-2'>
                                                                Chargeable Weight
                                                                <Info
                                                                    className='cursor-pointer'
                                                                    onClick={() => setPopWeightOpen && setPopWeightOpen(true)} />
                                                            </div>
                                                        </div>
                                                        <div className='flex gap-3'>
                                                            <div className='relative flex-[1]'>
                                                                <CustomInput
                                                                    type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                                                    value={(packingInfo.volumeWeight ? packingInfo.volumeWeight.toLocaleString() : '')}
                                                                    readonly
                                                                    onChange={() => { }}
                                                                />
                                                                <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                                    <span className='text-primary'>Kg</span>
                                                                </div>
                                                            </div>
                                                            <div className='relative flex-[1]'>
                                                                <CustomInput
                                                                    type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                                                    value={(packingInfo.actualWeight ? packingInfo.actualWeight.toLocaleString() : '')}
                                                                    readonly
                                                                    onChange={(e) => {
                                                                        setPackingInfo({
                                                                            ...packingInfo,
                                                                            actualWeight: Number(e.target.value)
                                                                        })
                                                                    }}
                                                                />
                                                                <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                                    <span className='text-primary'>Kg</span>
                                                                </div>
                                                            </div>
                                                            <div className='relative flex-[1]'>
                                                                <CustomInput
                                                                    type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                                                    value={(packingInfo.chargeableWeight ? packingInfo.chargeableWeight.toLocaleString() : '')}
                                                                    readonly
                                                                    onChange={(e) => {
                                                                        setPackingInfo({
                                                                            ...packingInfo,
                                                                            chargeableWeight: Number(e.target.value)
                                                                        })
                                                                    }}
                                                                />
                                                                <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                                                    <span className='text-primary'>Kg</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                        <div className='flex justify-center items-center'>
                                            <div
                                                className='flex justify-center items-center gap-2 bg-primary px-3 py-2 rounded w-full h-10 font-normal text-white text-sm text-center cursor-pointer'
                                                onClick={addPackingItem}
                                            >
                                                <AddW />
                                                <p>포장 목록에 포장 정보 추가하기</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </CreateArea>
                }

                {
                    quoteInfo?.step2?.packingList &&
                    quoteInfo?.step2?.packingList.length > 0 &&
                    <CreateArea title='포장정보목록' isStart>
                        <div className='flex flex-col gap-2 p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded w-full max-w-[724px] text-sm'>
                            <div className='tablet:hidden flex bg-[#EEF4FF]'>
                                {
                                    tHeader2.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex-[${item.flex}] flex items-center justify-center h-10`}
                                        >
                                            {item.label}
                                        </div>
                                    ))
                                }
                            </div>
                            {
                                quoteInfo?.step2?.packingList.map((item, idx) => (
                                    <div
                                        className={`flex items-center h-15 border border-[#E1E1E8] rounded  tablet:grid grid-cols-2 tablet:h-auto`}
                                        key={idx}>
                                        <div className='flex tablet:flex-col flex-[0.5] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[0].label}
                                            </p>
                                            {idx + 1}
                                        </div>
                                        <div className='flex tablet:flex-col flex-[1.5] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[1].label}
                                            </p>
                                            {item.packageType}
                                        </div>
                                        <div className='flex tablet:flex-col flex-[1] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[2].label}
                                            </p>
                                            {item.quantity}
                                        </div>
                                        <div className='flex tablet:flex-col flex-[1.5] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[3].label}
                                            </p>
                                            {item.width} * {item.height} * {item.length}
                                        </div>
                                        <div className='flex tablet:flex-col flex-[1] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[4].label}
                                            </p>
                                            {quoteInfo?.step1?.transportType === 'AIR' ? item.chargeableWeight : item.weight} Kg
                                        </div>
                                        <div className='flex tablet:flex-col flex-[1] justify-center items-center'>
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[5].label}
                                            </p>
                                            {item.isStackable ? '예' : '아니오'}
                                        </div>
                                        <div
                                            className='flex tablet:flex-col flex-[0.5] justify-center items-center'
                                            onClick={() => {
                                                setQuoteInfo(prev => ({
                                                    ...prev,
                                                    step2: {
                                                        ...prev.step2,
                                                        packingList: prev.step2.packingList.filter((_, i) => i !== idx)
                                                    }
                                                }));
                                            }}
                                        >
                                            <p className='hidden tablet:block bg-[#EEF4FF] w-full text-center'>
                                                {tHeader2[6].label}
                                            </p>
                                            <Bin />
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </CreateArea>
                }
                {
                    quoteInfo?.step1?.transportType === 'SEA' &&
                    !(quoteInfo.step2?.shipmentMode?.includes('FCL')) &&
                    <div className='flex items-start'>
                        <div className='tablet:hidden w-[123px]'></div>
                        <div className='relative flex-[1] p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg tablet:w-full max-w-[724px]'>
                            <div className='flex flex-col gap-3'>
                                <div className='flex gap-6'>
                                    <p className='flex-1'> 총 CBM</p>
                                    <p className='flex-1'>총 중량</p>
                                </div>
                                <div className='flex gap-3'>
                                    <div className='relative flex-[1]'>
                                        <CustomInput
                                            type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                            value={cbm > 0 ? cbm.toLocaleString() : ''}
                                            readonly
                                            onChange={(e) => {
                                            }}
                                        />
                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                            <span className='text-primary'>CBM</span>
                                        </div>
                                    </div>
                                    <div className='relative flex-[1]'>
                                        <CustomInput
                                            type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                            value={totalWeight > 0 ? totalWeight.toLocaleString() : ''}
                                            readonly
                                            onChange={(e) => {
                                            }}
                                        />
                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                            <span className='text-primary'>Kg</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                {
                    (quoteInfo?.step1?.transportType === 'AIR' ||
                        !(quoteInfo.step2?.shipmentMode?.includes('FCL'))) &&
                    <div className='flex items-start'>
                        <div className='tablet:hidden w-[123px]'></div>
                        <div className='relative flex-[1] p-7.5 tablet:p-4 border-[#9D9EB7] border-1 rounded-lg max-w-[724px]'>
                            <div className='flex flex-col gap-3'>
                                <div className='flex gap-6'>
                                    <p className='flex-1'> G C/W</p>
                                </div>
                                <div className='flex gap-3'>
                                    <div className='relative flex-[1]'>
                                        <CustomInput
                                            type="text" height={10} fontSize={'sm'} placeholder="자동계산"
                                            value={totalCW > 0 ? totalCW.toLocaleString() : ''}
                                            readonly
                                            onChange={(e) => {
                                            }}
                                        />
                                        <div className='top-0 right-3 absolute flex justify-center items-center h-full'>
                                            <span className='text-primary'>Kg</span>
                                        </div>
                                    </div>
                                    <div className='flex-1'></div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </AreaTitle>
    )
}

export default Third